import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import type { useUnifiedEngagementReview } from '../../../hooks/advisory/useUnifiedEngagementReview';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { hasReviewPreparationFailure } from '../../../hooks/advisory/advisoryReview';
import { meta } from '../../../styles/visualLanguage';

type Controller = ReturnType<typeof useUnifiedEngagementReview>;

export function AdvisoryReviewRecoveryControls({
  review,
  busy = false,
  variant = 'loading',
}: {
  review: Controller;
  busy?: boolean;
  variant?: 'loading' | 'draft' | 'profiles';
}) {
  const { t } = useUntypedTranslation();
  const snapshot = review.snapshot;
  const isBusy = Boolean(review.busy || busy);
  const reloadingScope = useRef<string | null>(null);
  if (
    reloadingScope.current !== review.scope ||
    review.readStatus === 'ready' ||
    review.readStatus === 'absent'
  )
    reloadingScope.current = null;
  const [recoveryOffered, setRecoveryOffered] = useState(false);
  useEffect(() => {
    setRecoveryOffered(false);
    const interrupted =
      snapshot?.status === 'preparing' ||
      (snapshot?.status === 'approved' &&
        ['pending', 'running'].includes(snapshot.downstream.status));
    if (!interrupted || isBusy) return undefined;
    const timer = window.setTimeout(() => setRecoveryOffered(true), 30000);
    return () => window.clearTimeout(timer);
  }, [review.scope, snapshot?.revision, snapshot?.status, snapshot?.downstream.status, isBusy]);

  // Approval closes editing, but another tab may have approved while this tab
  // still held a stale draft. Keep a discard action on both preparation views
  // so the obsolete edits cannot permanently block recovery.
  if (review.approved && review.stale) {
    return (
      <Alert severity="warning" data-testid="advisory-review-approved-elsewhere">
        {t('advisory:review.approvedElsewhere')}
        <Button disabled={isBusy} onClick={() => review.reconcile(false)}>
          {t('advisory:review.useApprovedReview')}
        </Button>
      </Alert>
    );
  }

  // Reconnecting the listener is independent of the pending preparation request.
  const readNotice = review.readError ? (
    <Alert severity="warning" data-testid="advisory-review-read-error">
      {t(
        review.readStatus === 'denied'
          ? 'advisory:review.readDenied'
          : review.readStatus === 'invalid'
          ? 'advisory:review.readInvalid'
          : 'advisory:review.readFailed'
      )}
      <Button
        data-testid="advisory-review-reload"
        disabled={variant !== 'draft' && isBusy && review.busy !== 'prepare'}
        onClick={() => {
          reloadingScope.current = review.scope;
          review.retryRead();
        }}
      >
        {t('advisory:review.readRetry')}
      </Button>
    </Alert>
  ) : null;
  // Restore access before offering a separate preparation or operation retry.
  if (readNotice) return readNotice;
  if (reloadingScope.current === review.scope && review.readStatus === 'loading') return null;
  const rejected = review.error === 'advisory:review.saveRejected';
  const failed = Boolean((review.error && !rejected) || snapshot?.downstream.status === 'failed');
  const needsPreparation =
    snapshot?.status === 'needsAttention' && hasReviewPreparationFailure(snapshot);
  const retryFailedOperation = failed && (variant !== 'draft' || !review.stale);
  const recoverDraft =
    variant === 'draft' && !isBusy && !rejected && (recoveryOffered || needsPreparation);
  const showRecovery = variant === 'draft' ? recoverDraft : recoveryOffered;
  if (!retryFailedOperation && !showRecovery) return null;

  const disabled =
    isBusy ||
    review.stale ||
    (!retryFailedOperation &&
      variant === 'draft' &&
      snapshot?.status !== 'preparing' &&
      (review.saving || review.dirty.length > 0));

  return (
    <>
      {variant === 'profiles' && failed && (
        <Typography role="alert" sx={[meta]}>
          {t('advisory:review.operationFailed')}
        </Typography>
      )}
      <Button
        data-testid={
          variant === 'draft' && !retryFailedOperation
            ? 'advisory-review-recover'
            : 'advisory-review-retry'
        }
        disabled={disabled}
        onClick={review.retry}
      >
        {t('advisory:review.retry')}
      </Button>
    </>
  );
}
